import { useContext, useEffect } from 'react'

import AnimateIntoView, {
  AnimationType,
} from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import ArticlePreviewCard from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { modalTypes } from '~/components/modalLoader'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'

import StatsCarousel from './StatsCarousel'

import styles from './SurveyIntro.module.css'

interface Stat {
  stat: string
  text: string
  image?: ImageProps
  points?: string[]
}

interface SurveyIntroProps {
  title?: string
  subtitle?: string
  // ctas?: any[]
  backgroundImage?: ImageProps
  stats?: Stat[]
  quote?: string
  aboutTitle?: string
  aboutIntro?: any
  aboutText?: any
  ctaImage?: ImageProps
  ctaTitle?: string
  ctaText?: string
  introVideo?: any
  videoText?: any
  formId?: string
}

const SurveyIntro = ({
  title,
  subtitle,
  // ctas,
  backgroundImage,
  stats,
  quote,
  aboutTitle,
  aboutIntro,
  aboutText,
  ctaImage,
  ctaTitle,
  ctaText,
  introVideo,
  videoText,
  formId,
}: SurveyIntroProps) => {
  const { state, stateDispatch } = useContext(GlobalStoreContext)

  // Debug logging to track modal state
  useEffect(() => {
    console.log('[SurveyIntro] Current modal state:', {
      isOpen: state.modal.isOpen,
      component: state.modal.component,
      formId: formId,
    })
  }, [state.modal, formId])

  const handleCTAClick = () => {
    stateDispatch({
      type: GlobalStoreProps.UPDATE_MODAL_STATE,
      payload: {
        isOpen: true,
        content: null,
        component: modalTypes.reportSignupModal,
        isDrawer: false,
        props: {
          title: 'Download Full Report',
          hideModalTitle: true,
          hideHeader: true,
          reducedWidth: true,
          reduceContentPadding: true,
          modifierClass: 'reportSignupModal',
          formId: formId || '',
          analyticsEventName: 'Print Collectors Survey Report Downloaded',
        },
      },
    })
  }

  return (
    <div className={styles.base}>
      {/* Hero Section with Background Image */}
      <div className={styles.heroSection}>
        {backgroundImage && (
          <div className={styles.backgroundImageContainer}>
            <div className={styles.backgroundImageWrapper}>
              <Image
                asset={backgroundImage.asset}
                alt=""
                objectFit="cover"
                priority
              />
            </div>
            <div className={styles.gradient} />
            <div className={styles.gradientBottom} />
          </div>
        )}

        <Container>
          <AnimateIntoView type={AnimationType.fadeIn} delay={0.2}>
            <div className={styles.heroContent}>
              {(title || subtitle) && (
                <div className={styles.intro}>
                  {title && (
                    <Text
                      Tag="h1"
                      className={styles.title}
                      size={TextSizes.primaryTitleLrg}
                    >
                      {title}
                    </Text>
                  )}
                  {subtitle && (
                    <Text
                      Tag="p"
                      className={styles.subtitle}
                      size={TextSizes.bodyMed}
                    >
                      {subtitle}
                    </Text>
                  )}

                  <div className={styles.ctas}>
                    <Button
                      iconLeft
                      variant={ButtonVariants.primaryLight}
                      button={{
                        type: ButtonTypes.button,
                        onClick: () => {
                          handleCTAClick()
                        },
                      }}
                    >
                      Download Full Report
                    </Button>
                  </div>
                </div>
              )}

              {stats && stats.length > 0 && <StatsCarousel stats={stats} />}
            </div>
          </AnimateIntoView>
        </Container>
      </div>

      {/* Quote Section */}
      {quote && (
        <Container>
          <AnimateIntoView type={AnimationType.fadeIn} delay={0.2}>
            {introVideo && (
              <section className={styles.quoteSection}>
                <div className={styles.videoContainer}>
                  {introVideo && (
                    <ArticlePreviewCard
                      {...(introVideo as any)}
                      video={{
                        ...introVideo,
                        summary: introVideo.summary || introVideo.keySummary,
                      }}
                      articleTypeSlug="video"
                      readtime={introVideo?.readtime || introVideo?.watchTime}
                      title={`${introVideo.title}`}
                      category="Guide"
                      imageType="landscape"
                      summaryLimit={200}
                      showReadMore={false}
                      hideSummary={true}
                      variant={'medium'}
                      theme={undefined}
                      leadImage={introVideo.leadImage}
                      hideAuthor={true}
                      titleTag="h3"
                      sizes="(max-width: 559px) 100vw, (max-width: 1079px) 33vw, 25vw"
                      noPrefetch={true}
                      openWatchModal
                      hideDetails
                      showPlayCircle
                      videoOnly
                    />
                  )}
                </div>
                <div className={styles.videoTextContainer}>
                  {aboutTitle && (
                    <Text
                      Tag="h2"
                      className={styles.aboutTitle}
                      size={TextSizes.primaryTitleMed}
                    >
                      {aboutTitle}
                    </Text>
                  )}

                  <Text
                    Tag="blockquote"
                    className={styles.quote}
                    size={TextSizes.secondaryTitleLrg}
                  >
                    {quote}
                  </Text>
                  {videoText && (
                    <div className={styles.videoText}>
                      <BlockContent value={videoText} />
                    </div>
                  )}
                </div>
              </section>
            )}
          </AnimateIntoView>
        </Container>
      )}

      {/* Combined About and CTA Section */}
      {(aboutIntro || aboutText || ctaImage || ctaTitle || ctaText) && (
        <section className={styles.aboutCtaSection}>
          <AnimateIntoView type={AnimationType.fadeIn} delay={0.2}>
            <Container>
              <div className={styles.aboutCtaWrapper}>
                {/* About the Report Column */}
                {(aboutIntro || aboutText) && (
                  <div className={styles.aboutColumn}>
                    {aboutIntro && (
                      <div className={styles.aboutIntro}>
                        <BlockContent
                          value={aboutIntro}
                          size={TextSizes.secondaryTitleLrg}
                        />
                      </div>
                    )}
                    {aboutText && (
                      <div className={styles.aboutText}>
                        <BlockContent value={aboutText} />
                      </div>
                    )}
                  </div>
                )}

                {/* CTA Column with Image */}
                {(ctaImage || ctaTitle || ctaText) && (
                  <div className={styles.ctaColumn}>
                    {ctaImage && (
                      <div className={styles.ctaImageWrapper}>
                        <Image
                          asset={ctaImage.asset}
                          alt=""
                          objectFit="cover"
                        />
                      </div>
                    )}
                    <div className={styles.ctaTextContent}>
                      {ctaTitle && (
                        <Text
                          Tag="h3"
                          className={styles.ctaTitle}
                          size={TextSizes.primaryTitleMed}
                        >
                          {ctaTitle}
                        </Text>
                      )}
                      {ctaText && (
                        <Text
                          Tag="p"
                          className={styles.ctaText}
                          size={TextSizes.bodyMed}
                        >
                          {ctaText}
                        </Text>
                      )}
                      <Button
                        iconLeft
                        variant={ButtonVariants.primaryDark}
                        button={{
                          type: ButtonTypes.button,
                          onClick: () => {
                            handleCTAClick()
                          },
                        }}
                      >
                        Download Full Report
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Container>
          </AnimateIntoView>
        </section>
      )}
    </div>
  )
}

export default SurveyIntro
